import { useTranslation } from 'react-i18next'
import { mdiEyeOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { apiBaseUrl } from '../../api/client'
import type { DeclarationProperties } from './declaration-fields'

type Props = {
    orgCode: string
    properties: DeclarationProperties
}

function documentUrl(orgCode: string, documentUid: string): string {
    return `${apiBaseUrl}/api/v1/organizations/${orgCode}/documents/${documentUid}/content`
}

/**
 * What the declarer actually reported, rendered where the syndic acts.
 *
 * Storing a voice note and three photographs that nobody can reach from the
 * ticket would be half a feature. This is the other half: the observed instant,
 * the two structured answers, the recording, and the photographs of the problem
 * — side by side, so triage happens on the declaration rather than on a
 * paraphrase of it.
 *
 * Renders nothing when the ticket carries no declaration payload, which is
 * every ticket a detector produced.
 */
export function DeclarationCard({ orgCode, properties }: Props) {
    const { t } = useTranslation('assets')

    const photos = properties.photo_document_uids ?? []
    const hasContent = Boolean(
        properties.occurred_at
        || properties.problem_type
        || properties.cause
        || properties.voice_note_document_uid
        || photos.length,
    )
    if (!hasContent) return null

    const observedAt = properties.occurred_at ? new Date(properties.occurred_at) : null
    const dateValid = observedAt && !isNaN(observedAt.getTime())

    return (
        <Card data-testid="declaration-card">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <AppIcon path={mdiEyeOutline} size="sm" />
                    {t('declare.card_title')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {dateValid && (
                        <div>
                            <dt className="text-xs text-muted-foreground">{t('declare.when')}</dt>
                            <dd className="text-sm text-foreground" data-testid="declaration-card-when">
                                {observedAt!.toLocaleString()}
                                {/* An assumed instant must never read as an observed
                                    one: on a claim the declaration clock runs from
                                    the observation. */}
                                {properties.occurred_at_source === 'filing_time' && (
                                    <Badge variant="outline" className="ml-2 font-normal" data-testid="declaration-card-when-assumed">
                                        {t('declare.when_assumed')}
                                    </Badge>
                                )}
                            </dd>
                        </div>
                    )}
                    {properties.problem_type && (
                        <div>
                            <dt className="text-xs text-muted-foreground">{t('declare.problem_type')}</dt>
                            <dd className="text-sm text-foreground" data-testid="declaration-card-problem-type">
                                {t(`declare.problem_type_${properties.problem_type}`)}
                            </dd>
                        </div>
                    )}
                    {properties.cause && (
                        <div>
                            <dt className="text-xs text-muted-foreground">{t('declare.cause')}</dt>
                            <dd className="text-sm text-foreground" data-testid="declaration-card-cause">
                                {t(`declare.cause_${properties.cause}`)}
                            </dd>
                        </div>
                    )}
                </dl>

                {properties.voice_note_document_uid && (
                    <div data-testid="declaration-card-voice-note">
                        <p className="mb-1 text-xs text-muted-foreground">{t('declare.voice_note')}</p>
                        <audio
                            controls
                            preload="none"
                            src={documentUrl(orgCode, properties.voice_note_document_uid)}
                            className="h-10 w-full max-w-sm"
                        />
                        {/* The machine's own version, kept beside the text the
                            declarer confirmed — the only way to tell a
                            mis-hearing from a mis-statement afterwards. */}
                        {properties.voice_transcript && (
                            <details className="mt-1">
                                <summary className="cursor-pointer text-xs text-muted-foreground">
                                    {t('declare.raw_transcript')}
                                </summary>
                                <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground" data-testid="declaration-card-raw-transcript">
                                    {properties.voice_transcript}
                                </p>
                            </details>
                        )}
                    </div>
                )}

                {photos.length > 0 && (
                    <div data-testid="declaration-card-photos">
                        <p className="mb-1 text-xs text-muted-foreground">{t('declare.photos')}</p>
                        <ul className="flex flex-wrap gap-2">
                            {photos.map((uid) => (
                                <li key={uid}>
                                    <a href={documentUrl(orgCode, uid)} target="_blank" rel="noopener noreferrer">
                                        <img
                                            src={documentUrl(orgCode, uid)}
                                            alt={t('declare.photo_alt')}
                                            loading="lazy"
                                            className="h-24 w-24 rounded-md border border-border object-cover transition-opacity hover:opacity-80"
                                        />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}
